/**
 * Activity Logging for Client Portal
 *
 * Centralized activity logging to Firestore (spesialis-project)
 * Same pattern as Landing page for consistency
 */
import { db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const SOURCE = 'client-portal'; // Identify this app as source

interface ActivityData {
    [key: string]: unknown;
}

/**
 * Log activity to centralized Firestore
 * @param type - Activity type (login, page_view, catalog_action, etc)
 * @param data - Additional data for the activity
 * @param userId - User/Client ID (optional)
 */
export async function logActivity(type: string, data: ActivityData = {}, userId?: string): Promise<void> {
    try {
        await addDoc(collection(db, 'activities'), {
            type,
            source: SOURCE,
            data,
            timestamp: serverTimestamp(),
            userId: userId || 'guest',
            path: typeof window !== 'undefined' ? window.location.pathname : '',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            screenWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
            screenHeight: typeof window !== 'undefined' ? window.innerHeight : 0
        });
    } catch (error) {
        // Silently fail - don't break the app for logging errors
        console.warn('Failed to log activity:', error);
    }
}

/**
 * Log client login
 */
export function logLogin(clientId: string, email: string): void {
    logActivity('client_login', { clientId, email }, clientId);
}

/**
 * Log client logout
 */
export function logLogout(clientId: string): void {
    logActivity('client_logout', { clientId }, clientId);
}

/**
 * Log page view in client portal
 */
export function logPageView(page: string, clientId?: string): void {
    logActivity('page_view', { page }, clientId);
}

/**
 * Log catalog action (import, edit, delete product)
 */
export function logCatalogAction(action: string, clientId: string, productCount?: number): void {
    logActivity('catalog_action', { action, productCount }, clientId);
}

/**
 * Log AI enrichment action
 */
export function logAIEnrichment(clientId: string, productId: string, success: boolean): void {
    logActivity('ai_enrichment', { productId, success }, clientId);
}

/**
 * Log kiosk interaction
 */
export function logKioskAction(action: string, clientId: string, query?: string): void {
    logActivity('kiosk_action', { action, query }, clientId);
}

/**
 * Log search in kiosk
 */
export function logKioskSearch(clientId: string, query: string, resultsCount: number): void {
    logActivity('kiosk_search', { query, resultsCount }, clientId);
}
